import { Injectable } from '@angular/core';
import { Message } from 'app/layout/common/messages/messages.types';
import { map, Observable, ReplaySubject, switchMap, take, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MessagesService {
    private _messages: ReplaySubject<Message[]> = new ReplaySubject<Message[]>(
        1
    );

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for messages
     */
    get messages$(): Observable<Message[]> {
        return this._messages.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all messages
     */
    getAll(): Observable<Message[]> {
        const seed: Message[] = [];
        this._messages.next(seed);
        return of(seed);
    }

    /**
     * Create a message
     *
     * @param message
     */
    create(message: Message): Observable<Message> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                of({ ...message }).pipe(
                    map((newMessage) => {
                        this._messages.next([...messages, newMessage]);
                        return newMessage;
                    })
                )
            )
        );
    }

    /**
     * Update the message
     *
     * @param id
     * @param message
     */
    update(id: string, message: Message): Observable<Message> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                of({ ...message, id }).pipe(
                    map((updatedMessage: Message) => {
                        const index = messages.findIndex((item) => item.id === id);
                        if (index > -1) {
                            messages[index] = updatedMessage;
                        }
                        this._messages.next([...messages]);
                        return updatedMessage;
                    })
                )
            )
        );
    }

    /**
     * Delete the message
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                of(true).pipe(
                    map((isDeleted: boolean) => {
                        const index = messages.findIndex((item) => item.id === id);
                        if (index > -1) {
                            messages.splice(index, 1);
                        }
                        this._messages.next([...messages]);
                        return isDeleted;
                    })
                )
            )
        );
    }

    /**
     * Mark all messages as read
     */
    markAllAsRead(): Observable<boolean> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                of(true).pipe(
                    map((isUpdated: boolean) => {
                        const updated = messages.map((m) => ({ ...m, read: true }));
                        this._messages.next(updated);
                        return isUpdated;
                    })
                )
            )
        );
    }
}
